import type { ClientAnalyticsEvent } from "@townsy/contracts";

/**
 * Batches measurement candidates and posts them every few seconds (brief section 5: batch POST
 * every 5 s, max 25 events per batch per the `ClientAnalyticsBatch` contract). Candidates are
 * best-effort: a failed batch is retried once, then dropped. Nothing here is authoritative.
 * Nothing is posted before `start()`: events pushed earlier wait (the server drops candidates from a
 * visitor without a live room presence, so the session starts the queue once the room is joined).
 */
export interface AnalyticsQueueOptions {
  post(events: ClientAnalyticsEvent[], opts: { keepalive?: boolean }): Promise<unknown>;
  intervalMs?: number;
  maxBatch?: number;
  maxQueued?: number;
  maxAttempts?: number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  onError?(err: unknown): void;
}

interface Queued {
  event: ClientAnalyticsEvent;
  attempts: number;
}

export class AnalyticsQueue {
  private queue: Queued[] = [];
  private timer: unknown = null;
  private inFlight = false;
  private started = false;
  private readonly intervalMs: number;
  private readonly maxBatch: number;
  private readonly maxQueued: number;
  private readonly maxAttempts: number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;

  constructor(private readonly opts: AnalyticsQueueOptions) {
    this.intervalMs = opts.intervalMs ?? 5000;
    this.maxBatch = opts.maxBatch ?? 25;
    this.maxQueued = opts.maxQueued ?? 200;
    this.maxAttempts = opts.maxAttempts ?? 2;
    this.setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = opts.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  get size(): number {
    return this.queue.length;
  }

  push(event: ClientAnalyticsEvent): void {
    this.queue.push({ event, attempts: 0 });
    if (this.queue.length > this.maxQueued) this.queue.splice(0, this.queue.length - this.maxQueued);
    this.schedule();
  }

  start(): void {
    this.started = true;
    this.schedule();
  }

  stop(): void {
    this.started = false;
    if (this.timer !== null) {
      this.clearTimer(this.timer);
      this.timer = null;
    }
  }

  private schedule(): void {
    if (!this.started || this.timer !== null || this.queue.length === 0) return;
    this.timer = this.setTimer(() => {
      this.timer = null;
      void this.flush();
    }, this.intervalMs);
  }

  /** Send one batch now. `keepalive` lets the request outlive a closing page. */
  async flush(opts: { keepalive?: boolean } = {}): Promise<void> {
    if (this.inFlight || this.queue.length === 0) return;
    const batch = this.queue.splice(0, this.maxBatch);
    this.inFlight = true;
    try {
      await this.opts.post(
        batch.map((q) => q.event),
        opts,
      );
    } catch (err) {
      const retry = batch.filter((q) => q.attempts + 1 < this.maxAttempts).map((q) => ({ ...q, attempts: q.attempts + 1 }));
      this.queue.unshift(...retry);
      this.opts.onError?.(err);
    } finally {
      this.inFlight = false;
    }
    if (this.queue.length > 0) this.schedule();
  }
}
